import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import { useSelector } from 'react-redux';
import MainLayout from '../../layouts/MainLayout';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faPlus,
  faEye,
  faPen,
  faTrash,
  faPlay,
  faDownload,
  faList,
  faFolderOpen,
  faFilePowerpoint,
} from '@fortawesome/free-solid-svg-icons';

const VisualBriefLibrary = () => {
  const authState = useSelector((state) => state.authReducer);
  const navigate = useNavigate();
  const location = useLocation();
  const [fetchData, { loading }] = useFetch();
  const [vbMasters, setVbMasters] = useState([]);
  const [selectedVbMasterId, setSelectedVbMasterId] = useState('');
  const fromRouteInitialized = useRef(false);

  const fromRoute = (location?.pathname || '').split('/').pop() || '';
  const [toRoute, setToRoute] = useState('');
  useEffect(() => {
    if (fromRoute && !fromRouteInitialized.current) {
      let toRoute = '';
      switch (fromRoute) {
        case 'visualbrief-myvb':
          toRoute = 'visualbrief-myvb';
          break;
        case 'visualbrief-publicvb':
          toRoute = 'visualbrief-publicvb';
          break;
        case 'visualbrief-vbforme':
          toRoute = 'visualbrief-vbforme';
          break;
        default:
          toRoute = 'visualbrief-myvb';
          break;
      }
      setToRoute(toRoute);
      fromRouteInitialized.current = true;
    }
  }, [location.pathname]);

  // vbMasterList
  const fetchvbMasterList = useCallback(() => {
    let config = '';
    // console.log('fromRoute ' + fromRoute);
    if (fromRoute === 'visualbrief-myvb') {
      config = {
        url: '/vbmaster/my',
        method: 'post',
        headers: { Authorization: authState.token },
        data: { userId: authState.user._id },
      };
    } else {
      config = {
        url: '/vbmaster/public',
        method: 'post',
        headers: { Authorization: authState.token },
      };
    }
    // console.log('sending vb deck ' + fromRoute + ' master query: ', config);

    fetchData(config, { showSuccessToast: false })
      .then((response) => {
        if (Array.isArray(response.vbMasters)) {
          setVbMasters(response.vbMasters);
        } else {
          console.error('Invalid response format: VisualBrief Master list not found in response');
        }
      })
      .catch((error) => {
        console.error('Error fetching VisualBrief Master list:', error);
      });
  }, [authState.token, fetchData, fromRoute]);

  useEffect(() => {
    fetchvbMasterList();
  }, [fetchvbMasterList]);

  const handleVbMasterDescView = (itemId) => {
    setSelectedVbMasterId((prevId) => (prevId === itemId ? null : itemId));
  };

  const handleStartClick = (vbMasterId, vbTitle, noOfSlides, isPresenting) => {
    navigate('/vbslideshow', {
      state: { vbMasterId: vbMasterId, title: vbTitle, noOfSlides: noOfSlides, isPresenting: isPresenting },
    });
  };

  const handleManageSlidesClick = (vbMasterId, vbTitle, noOfSlides, isPresenting) => {
    navigate('/vbslidelist', {
      state: { vbMasterId: vbMasterId, title: vbTitle, noOfSlides: noOfSlides, isPresenting: isPresenting },
    });
  };

  const handleDownloadClick = (vbMasterId, vbTitle, noOfSlides, isPresenting) => {
    navigate('/vbdownload', {
      state: { vbMasterId: vbMasterId, title: vbTitle, noOfSlides: noOfSlides, isPresenting: isPresenting },
    });
  };

  const handleAccessLogClick = (vbMasterId, vbTitle) => {
    navigate('/vbaccessors', { state: { vbMasterId: vbMasterId, title: vbTitle } });
  };

  const handleDownloadLogClick = (vbMasterId, vbTitle) => {
    navigate('/vbdownloaders', { state: { vbMasterId: vbMasterId, title: vbTitle } });
  };

  const handleDeleteClick = (id) => {
    const config = {
      url: `/vbmaster/${id}`,
      method: 'delete',
      headers: { Authorization: authState.token },
    };
    fetchData(config)
      .then(() => {
        setVbMasters(vbMasters.filter((vbmaster) => vbmaster._id !== id));
      })
      .catch((error) => {
        console.error('Failed to delete the record:', error);
      });
  };

  const renderActionIcon = (icon, onClick, tooltipText, disabled = false, className = '') => (
    <Tooltip text={tooltipText} position="top">
      <span
        className={`p-2 rounded-full transition-colors duration-200 cursor-pointer ${
          disabled
            ? 'text-slate-500 bg-slate-700'
            : `text-teal-400 hover:bg-teal-500/20 hover:text-teal-300 ${className}`
        }`}
        onClick={disabled ? null : onClick}
      >
        <FontAwesomeIcon icon={icon} className="text-sm" />
      </span>
    </Tooltip>
  );

  // Skeleton Card Component
  const SkeletonCard = () => (
    <article className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg overflow-hidden animate-pulse">
      {/* Card Header */}
      <div className="p-4">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-slate-600 rounded-full"></div>
            <div className="h-5 bg-slate-600 rounded w-3/4"></div>
          </div>
          <div className="flex flex-wrap items-center gap-1 sm:gap-2 justify-start sm:justify-end min-w-0">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="w-8 h-8 bg-slate-600 rounded-full"></div>
            ))}
          </div>
        </div>
      </div>
    </article>
  );

  return (
    <div>
      <div className="min-h-screen bg-black py-4 px-2 sm:px-4">
        {/* Header */}
        <header className="flex flex-col sm:flex-row items-start mb-2 border border-teal-500 bg-black">
          <h3 className="text-xl lg:text-3xl font-bold text-teal-300">VisualBrief Presentations</h3>
        </header>
        <div className='flex-1 justify-end mb-2'>
            <Link
                to="/vbmaster/add"
                className="w-32 text:sm flex items-end justify-end gap-2 mt-4 px-4 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg shadow-lg transition-all duration-200 font-medium"
            >
                <FontAwesomeIcon className="mt-2" icon={faPlus} />
                <span>Add New</span>
            </Link>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, index) => (
              <SkeletonCard key={index} />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {vbMasters && vbMasters.length === 0 ? (
              <div className="min-h-screen flex items-center justify-center text-slate-400 text-lg font-medium">
                No records found!
              </div>
            ) : (
              vbMasters.map((vbmaster, index) => (
                <article
                  key={vbmaster._id}
                  className="bg-slate-800 border border-slate-700 rounded-lg shadow-lg overflow-hidden transition-all duration-200 hover:shadow-teal-500/25"
                >
                  {/* Card Header */}
                  <div className="p-4 border border-teal-500">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                      <h4 className="text-m lg:text-lg text-slate-200 flex items-center gap-2">
                        {index + 1}. {vbmaster.title}
                      </h4>
                      <div className="flex flex-wrap items-center gap-1 sm:gap-2 justify-start sm:justify-end min-w-0">
                        {renderActionIcon(
                          faEye,
                          () => handleVbMasterDescView(vbmaster._id),
                          'View details'
                        )}
                        {fromRoute === 'visualbrief-myvb' && (
                          <>
                            {renderActionIcon(
                              faPen,
                              () => navigate(`/vbmaster/${vbmaster._id}`),
                              'Edit this record',
                              false,
                              'text-green-400 hover:bg-green-500/20 hover:text-green-300'
                            )}
                            {renderActionIcon(
                              faTrash,
                              () => handleDeleteClick(vbmaster._id),
                              'Delete',
                              vbmaster.isPrepared
                            )}
                          </>
                        )}
                        {renderActionIcon(
                          faPlay,
                          () => handleStartClick(vbmaster._id, vbmaster.title, vbmaster.noOfSlides, vbmaster.isPresenting),
                          'View Presentation',
                          !vbmaster.isPrepared
                        )}
                        {vbmaster.canDownload &&
                          renderActionIcon(
                            faDownload,
                            () => handleDownloadClick(vbmaster._id, vbmaster.title, vbmaster.noOfSlides, vbmaster.isPresenting),
                            'Download Presentation'
                          )}
                        {renderActionIcon(
                          faList,
                          () => handleAccessLogClick(vbmaster._id, vbmaster.title),
                          'Access Log'
                        )}
                        {renderActionIcon(
                          faFolderOpen,
                          () => handleDownloadLogClick(vbmaster._id, vbmaster.title),
                          'Downloads Log'
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {selectedVbMasterId === vbmaster._id && (
                    <div className="bg-slate-700 border-t border-slate-600 px-4 pb-4 pt-0 animate-fadeIn">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                        <p className="text-m lg:text-lg text-yellow-200 mt-2">
                          <strong>{vbmaster.title}:</strong> 
                        </p>
                        <button
                          onClick={() =>
                            handleManageSlidesClick(vbmaster._id, vbmaster.title, vbmaster.noOfSlides, vbmaster.isPresenting)
                          }
                          className="flex items-center justify-center w-40 gap-2 px-4 py-2 mt-2 bg-teal-500 hover:bg-teal-600 text-white rounded-lg shadow-md transition-all duration-200 font-medium"
                        >
                          <FontAwesomeIcon icon={faFilePowerpoint} />
                          <span className="text-m lg:text=lg">Manage Slides</span>
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-slate-300">
                        <p>
                          <strong>Purpose:</strong> {vbmaster.purpose}
                        </p>
                        <p>
                          <strong>Date Created:</strong>{' '}
                          <FontAwesomeIcon icon={faCalendarAlt} className="text-teal-400 mr-1" />
                          {new Date(vbmaster.dateCreated).toLocaleDateString()}
                        </p>
                        <p>
                          <strong>Description:</strong> {vbmaster.desc}
                        </p>
                        <p>
                          <strong>#Slides:</strong> {vbmaster.noOfSlides}
                        </p>
                        <p>
                          <strong>Has Cover Image?:</strong> {vbmaster.hasCoverImage ? 'Yes' : 'No'}
                        </p>
                        <p>
                          <strong>Can Download?:</strong> {vbmaster.canDownload ? 'Yes' : 'No'}
                        </p>
                        <p>
                          <strong>Presentation is Public?:</strong> {vbmaster.isPublic ? 'Yes' : 'No'}
                        </p>
                        <p>
                          <strong>Presentation is For Select Individuals?:</strong> {vbmaster.forIndividual ? 'Yes' : 'No'}
                        </p>
                        <p>
                          <strong>Presentation is For Select Groups?:</strong> {vbmaster.forGroup ? 'Yes' : 'No'}
                        </p>
                        <p>
                          <strong>Book Title:</strong> {vbmaster.bookTitle}
                        </p>
                        <p>
                          <strong>Is Active?:</strong> {vbmaster.isActive ? 'Yes' : 'No'}
                        </p>
                        <p>
                          <strong>Ready for Presentation?:</strong> {vbmaster.isPrepared ? 'Yes' : 'No'}
                        </p>
                      </div>
                    </div>
                  )}
                </article>
              ))
            )}
          </div>
        )}
      </div>
       <footer className="bg-black border border-teal-500 text-white py-4">
            <div className=" mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="text-sm lg:text-lg text-yellow-200">
                © 2025 Think5Whys Global DeepTech Solutions. All rights reserved.
            </div>
            <div className="flex flex-wrap gap-4 text-sm lg:text-lg">
                <Link to="/about-us" className="text-white hover:text-pink-400">About</Link>
                <Link to="/privacy-policy" className="text-white hover:text-pink-400">Privacy Policy</Link>
                <Link to="/terms-of-use" className="text-white hover:text-pink-400">Terms of Use</Link>
                <Link to="/contact-us" className="text-white hover:text-pink-400">Contact</Link>
            </div>
            </div>
        </footer>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            max-height: 0;
          }
          to {
            opacity: 1;
            max-height: 500px;
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default VisualBriefLibrary;